import React, { useState } from 'react';
import {
    Container,
    Header,
    SpaceBetween,
    Button,
    Alert,
    Box,
    Modal,
    Table,
    StatusIndicator,
    StatusIndicatorProps,
    ColumnLayout
} from '@cloudscape-design/components';
import { marketCapApi } from '../../client/clients';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFlashbar } from '../../context/FlashbarContext';
import { classifyError, alertTypeForKind } from '../../util/errorClassifier';

interface MarketCapEntry {
    symbol: string;
    marketCap: number | null;
    exchange: string;
    name: string;
}

interface MarketCapData {
    bucketName: string;
    s3Key: string;
    lastModified: string;
    sizeBytes: number;
    data: MarketCapEntry[];
    totalRecords: number;
}

interface LambdaStatus {
    functionName: string;
    state: string;
    stateReason?: string;
    lastUpdateStatus: string;
    lastModified: string;
    isRunning: boolean;
}

export function MarketCapManagementPage() {
    useDocumentTitle('Market cap data');
    const flashbar = useFlashbar();
    const [triggering, setTriggering] = useState(false);
    const [triggerResult, setTriggerResult] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    // T4-D: confirmation modal state before firing the update.
    const [triggerConfirmOpen, setTriggerConfirmOpen] = useState(false);
    
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [lambdaStatus, setLambdaStatus] = useState<LambdaStatus | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    
    const [loadingData, setLoadingData] = useState(false);
    const [marketCapData, setMarketCapData] = useState<MarketCapData | null>(null);
    const [dataError, setDataError] = useState<string | null>(null);

    const handleTriggerUpdate = async () => {
        setTriggerConfirmOpen(false);
        setTriggering(true);
        setTriggerResult(null);

        try {
            const response = await marketCapApi.triggerMarketCapUpdate();
            setTriggerResult({
                type: 'success',
                message: response.data || 'Market cap update triggered successfully'
            });
            flashbar.push({
                type: 'success',
                header: 'Market-cap update triggered',
                content: 'The collector is running. Refresh status in a few minutes to see the new run.',
            });
        } catch (error: any) {
            console.error('Error triggering market cap update:', error);
            const message = error.response?.data?.message || 'Failed to trigger market cap update';
            setTriggerResult({ type: 'error', message });
            flashbar.push({
                type: 'error',
                header: 'Failed to trigger market-cap update',
                content: message,
            });
        } finally {
            setTriggering(false);
        }
    };

    const handleGetStatus = async () => {
        setLoadingStatus(true);
        setStatusError(null);
        
        try {
            const response = await marketCapApi.getLambdaStatus();
            setLambdaStatus(response.data as LambdaStatus);
        } catch (error: any) {
            console.error('Error getting Lambda status:', error);
            setStatusError(error.response?.data?.message || 'Failed to load market-cap status');
        } finally {
            setLoadingStatus(false);
        }
    };

    const handleGetData = async () => {
        setLoadingData(true);
        setDataError(null);
        
        try {
            const response = await marketCapApi.getMarketCapData();
            setMarketCapData(response.data as MarketCapData);
        } catch (error: any) {
            console.error('Error getting market cap data:', error);
            setDataError(error.response?.data?.message || 'Failed to get market cap data');
        } finally {
            setLoadingData(false);
        }
    };

    const formatBytes = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const formatMarketCap = (marketCap: number | null): string => {
        if (marketCap === null) return 'N/A';
        if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(2)}T`;
        if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
        if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
        return `$${marketCap.toLocaleString()}`;
    };

    const formatDate = (dateString: string): string => {
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    const getTimeSinceUpdate = (lastModified: string): string => {
        try {
            const now = new Date().getTime();
            const lastUpdate = new Date(lastModified).getTime();
            const diffMs = now - lastUpdate;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);
            const diffDays = Math.floor(diffHours / 24);

            if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
            if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
            if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
            return 'Just now';
        } catch {
            return 'Unknown';
        }
    };

    return (
        <Container>
            <SpaceBetween size="l">
                <Header variant="h1">
                    Market cap data
                </Header>

                {/* Trigger Update Section — I2 body copy. */}
                <SpaceBetween size="m">
                    <Header
                        variant="h2"
                        description="Manually run the market-cap data update."
                    >
                        Trigger data update
                    </Header>

                    {triggerResult && (
                        <Alert
                            type={triggerResult.type}
                            dismissible
                            onDismiss={() => setTriggerResult(null)}
                        >
                            {triggerResult.message}
                        </Alert>
                    )}

                    <SpaceBetween direction="horizontal" size="m" alignItems="center">
                        <Button
                            variant="primary"
                            iconName="upload"
                            loading={triggering}
                            onClick={() => setTriggerConfirmOpen(true)}
                            data-testid="trigger-update-btn"
                        >
                            Trigger update
                        </Button>
                        {/* T4-D: show last-run timestamp when lambdaStatus is available. */}
                        {lambdaStatus?.lastModified && (
                            <Box variant="small" color="text-body-secondary">
                                Last run: {formatDate(lambdaStatus.lastModified)}
                            </Box>
                        )}
                    </SpaceBetween>
                </SpaceBetween>

                {/* T4-D: confirmation modal before triggering the update. */}
                <Modal
                    visible={triggerConfirmOpen}
                    onDismiss={() => setTriggerConfirmOpen(false)}
                    header="Run market-cap data update now?"
                    data-testid="trigger-confirm-modal"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="link"
                                    onClick={() => setTriggerConfirmOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    loading={triggering}
                                    onClick={handleTriggerUpdate}
                                    data-testid="trigger-confirm-run-btn"
                                >
                                    Run update
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    This will fetch fresh data and may consume API quota.
                </Modal>

                {/* Lambda Status Section — I1: user-friendly labels, hide raw infra fields. */}
                <SpaceBetween size="m">
                    <Header
                        variant="h2"
                        description="Check the current status of the data-update job."
                        actions={
                            <Button
                                iconName="refresh"
                                loading={loadingStatus}
                                onClick={handleGetStatus}
                            >
                                Refresh status
                            </Button>
                        }
                    >
                        Job status
                    </Header>

                    {/* I4 + A6: classify the failure for actionable copy + suggested action. */}
                    {statusError && (() => {
                        const classified = classifyError(new Error(statusError), { onRetry: handleGetStatus });
                        return (
                            <Alert
                                type={alertTypeForKind(classified.kind)}
                                header={classified.userMessage}
                                dismissible
                                onDismiss={() => setStatusError(null)}
                                action={
                                    classified.suggestedAction ? (
                                        <Button
                                            onClick={classified.suggestedAction.onClick}
                                            href={classified.suggestedAction.href}
                                            loading={loadingStatus}
                                        >
                                            {classified.suggestedAction.label}
                                        </Button>
                                    ) : (
                                        <Button onClick={handleGetStatus} loading={loadingStatus}>
                                            Retry
                                        </Button>
                                    )
                                }
                            />
                        );
                    })()}

                    {lambdaStatus && (() => {
                        // I1: translate raw Lambda state into a user-facing concept.
                        // STOPPED/Inactive → idle (waiting for next scheduled run); RUNNING → in-progress; failed → error.
                        let statusType: StatusIndicatorProps.Type;
                        let statusLabel: string;
                        if (lambdaStatus.isRunning) {
                            statusType = 'in-progress';
                            statusLabel = 'Running';
                        } else if (lambdaStatus.lastUpdateStatus === 'Failed' || lambdaStatus.state === 'Failed') {
                            statusType = 'error';
                            statusLabel = 'Failed';
                        } else if (lambdaStatus.state === 'Pending') {
                            statusType = 'pending';
                            statusLabel = 'Pending';
                        } else {
                            statusType = 'stopped';
                            statusLabel = 'Idle (waiting for next scheduled run)';
                        }

                        // TODO(I1): expose `lastRun` / `nextScheduled` from the backend
                        // so we can render "Last run" / "Next scheduled" without leaking
                        // raw Lambda fields. Until then, only show the friendly status.
                        return (
                            <ColumnLayout columns={2} variant="text-grid">
                                <SpaceBetween size="xs">
                                    <Box variant="awsui-key-label">Status</Box>
                                    <StatusIndicator type={statusType}>{statusLabel}</StatusIndicator>
                                </SpaceBetween>
                                <SpaceBetween size="xs">
                                    <Box variant="awsui-key-label">Last update</Box>
                                    <div>
                                        {formatDate(lambdaStatus.lastModified)}
                                    </div>
                                </SpaceBetween>
                            </ColumnLayout>
                        );
                    })()}

                    {!lambdaStatus && !statusError && !loadingStatus && (
                        <Box textAlign="center" color="text-body-secondary" padding="l">
                            Click "Refresh status" to view the job status.
                        </Box>
                    )}
                </SpaceBetween>

                {/* Market Cap Data Section — I2 body copy. */}
                <SpaceBetween size="m">
                    <Header
                        variant="h2"
                        description="View collected market cap data."
                        actions={
                            <Button
                                iconName="download"
                                loading={loadingData}
                                onClick={handleGetData}
                            >
                                Load data
                            </Button>
                        }
                    >
                        Market cap data
                    </Header>

                    {dataError && (() => {
                        const classified = classifyError(new Error(dataError), { onRetry: handleGetData });
                        return (
                            <Alert
                                type={alertTypeForKind(classified.kind)}
                                header={classified.userMessage}
                                dismissible
                                onDismiss={() => setDataError(null)}
                                action={
                                    classified.suggestedAction ? (
                                        <Button
                                            onClick={classified.suggestedAction.onClick}
                                            href={classified.suggestedAction.href}
                                            loading={loadingData}
                                        >
                                            {classified.suggestedAction.label}
                                        </Button>
                                    ) : (
                                        <Button onClick={handleGetData} loading={loadingData}>
                                            Retry
                                        </Button>
                                    )
                                }
                            />
                        );
                    })()}
                    
                    {marketCapData && (
                        <SpaceBetween size="m">
                            {/* I3: bucket name is internal infra; surface the user-facing fields only. */}
                            <ColumnLayout columns={3} variant="text-grid">
                                <SpaceBetween size="xs">
                                    <Box variant="awsui-key-label">Total Records</Box>
                                    <div>{marketCapData.totalRecords.toLocaleString()}</div>
                                </SpaceBetween>
                                <SpaceBetween size="xs">
                                    <Box variant="awsui-key-label">File Size</Box>
                                    <div>{formatBytes(marketCapData.sizeBytes)}</div>
                                </SpaceBetween>
                                <SpaceBetween size="xs">
                                    <Box variant="awsui-key-label">Last Updated</Box>
                                    <div>
                                        {formatDate(marketCapData.lastModified)}
                                        <br />
                                        <Box variant="small" color="text-body-secondary">
                                            ({getTimeSinceUpdate(marketCapData.lastModified)})
                                        </Box>
                                    </div>
                                </SpaceBetween>
                            </ColumnLayout>
                            
                            <Table
                                columnDefinitions={[
                                    {
                                        id: 'symbol',
                                        header: 'Symbol',
                                        cell: (item: MarketCapEntry) => item.symbol,
                                        sortingField: 'symbol'
                                    },
                                    {
                                        id: 'name',
                                        header: 'Company Name',
                                        cell: (item: MarketCapEntry) => item.name,
                                        sortingField: 'name'
                                    },
                                    {
                                        id: 'exchange',
                                        header: 'Exchange',
                                        cell: (item: MarketCapEntry) => item.exchange,
                                        sortingField: 'exchange'
                                    },
                                    {
                                        id: 'marketCap',
                                        header: 'Market Cap',
                                        cell: (item: MarketCapEntry) => formatMarketCap(item.marketCap),
                                        sortingField: 'marketCap'
                                    }
                                ]}
                                items={marketCapData.data.slice(0, 100)} // Show first 100 for performance
                                loadingText="Loading market cap data"
                                sortingDisabled={false}
                                variant="container"
                                stickyHeader={true}
                                stripedRows
                                header={
                                    <Header
                                        counter={`(${Math.min(100, marketCapData.data.length)} of ${marketCapData.totalRecords})`}
                                    >
                                        Market Cap Records
                                    </Header>
                                }
                                empty={
                                    <Box textAlign="center" color="inherit">
                                        <b>No records</b>
                                        <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                                            No market cap data available.
                                        </Box>
                                    </Box>
                                }
                            />
                            
                            {marketCapData.data.length > 100 && (
                                <Alert type="info">
                                    Showing first 100 records out of {marketCapData.totalRecords.toLocaleString()} total records for performance. 
                                    All data is loaded and available for use by the application.
                                </Alert>
                            )}
                        </SpaceBetween>
                    )}
                    
                    {!marketCapData && !dataError && !loadingData && (
                        <Box textAlign="center" color="text-body-secondary" padding="l">
                            Click "Load data" to view market cap data.
                        </Box>
                    )}
                </SpaceBetween>
            </SpaceBetween>
        </Container>
    );
}
